/**
 * How a meeting repeats — Outlook's options (Aaron, Oct 23: "meetings need
 * more robust recurring options … use the ones Outlook has").
 *
 * **Every N days, weeks (on chosen weekdays), months or years**; a month or
 * a year either on the same day number or on the *nth weekday* of the first
 * date (*the fourth Thursday*, *the last Friday*); **ending never, on a date,
 * or after N occurrences**. Stored as columns beside `repeat_rule` (migration
 * 0138): `repeat_rule` is the frequency, `repeat_interval`, `repeat_weekdays`,
 * `repeat_month_nth`, `repeat_until` and `repeat_count` the rest.
 *
 * **One expansion** — `seriesDates` — read by the calendar, the task
 * lists' *meets* and *next*, a repeating task's next due date and the words,
 * so nothing anywhere disagrees about which days a series falls on. Dates are
 * local calendar days (`YYYY-MM-DD`); the time of day is the series' own and
 * is put back in the zone by the caller, so a 9:00 meeting is 9:00 on both
 * sides of a clock change.
 *
 * As Outlook does, a week starts on Sunday, and a day number a month does not
 * have (the 31st) falls on its last day.
 */
import { addDays } from '../calendar.js';
import { clock, clockRange, dayComma } from '../requests/words.js';
export const REPEAT_FREQS = ['daily', 'weekly', 'monthly', 'yearly'];
/** *the first … the fourth*, or *the last*. */
export const REPEAT_NTHS = [1, 2, 3, 4, -1];
export const REPEAT_MAX_INTERVAL = 99;
export const REPEAT_MAX_COUNT = 999;
/** Monday to Friday — *Every weekday* (Aaron, Oct 24, note 1.3), as Outlook offers it. */
export const WORK_WEEKDAYS = [1, 2, 3, 4, 5];
/** True when a week's days are Monday to Friday and nothing else. */
export function isWorkWeekdays(days) {
    return Boolean(days) && days.length === WORK_WEEKDAYS.length && WORK_WEEKDAYS.every((day) => days.includes(day));
}
/**
 * The stored columns as a recurrence, or null when it does not repeat.
 * 0136's `every_other_week` reads as every 2 weeks, which is what it said.
 */
export function recurrenceOf(stored) {
    if (!stored.rule)
        return null;
    const legacy = stored.rule === 'every_other_week';
    const freq = (legacy ? 'weekly' : stored.rule);
    if (!REPEAT_FREQS.includes(freq))
        return null;
    const nth = stored.monthNth ?? null;
    return {
        freq,
        interval: legacy ? 2 : Math.max(1, Math.min(REPEAT_MAX_INTERVAL, stored.interval ?? 1)),
        weekdays: freq === 'weekly' && stored.weekdays && stored.weekdays.length > 0
            ? [...new Set(stored.weekdays)].filter((day) => day >= 0 && day <= 6).sort((a, b) => a - b) : null,
        monthNth: (freq === 'monthly' || freq === 'yearly') && nth !== null && REPEAT_NTHS.includes(nth)
            ? nth : null,
        until: stored.until ? stored.until.slice(0, 10) : null,
        count: stored.count ?? null,
    };
}
/** The columns a recurrence is stored as (the inverse of `recurrenceOf`). */
export function storedRepeat(recurrence) {
    if (!recurrence) {
        return { repeatRule: null, repeatInterval: 1, repeatWeekdays: null, repeatMonthNth: null, repeatUntil: null, repeatCount: null };
    }
    /* The *Every other week* preset is stored as 0136 stored it, so every reader of the rule alone still says it. */
    if (presetKeyOf(recurrence) === 'every_other_week') {
        return { repeatRule: 'every_other_week', repeatInterval: 1, repeatWeekdays: null, repeatMonthNth: null, repeatUntil: null, repeatCount: null };
    }
    return {
        repeatRule: recurrence.freq,
        repeatInterval: recurrence.interval,
        repeatWeekdays: recurrence.freq === 'weekly' ? recurrence.weekdays : null,
        repeatMonthNth: recurrence.freq === 'monthly' || recurrence.freq === 'yearly' ? recurrence.monthNth : null,
        repeatUntil: recurrence.until,
        repeatCount: recurrence.until ? null : recurrence.count,
    };
}
// ---------------------------------------------------------------------------
// Day arithmetic on YYYY-MM-DD
// ---------------------------------------------------------------------------
function parts(date) {
    return date.split('-').map(Number);
}
function dayString(year, month, day) {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/** 0 = Sunday … 6 = Saturday. */
export function weekdayIndex(date) {
    return new Date(`${date}T00:00:00Z`).getUTCDay();
}
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function daysBetween(from, to) {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}
/** Which of its weekday a date is in its month: 1–5. */
export function nthOfMonth(date) {
    return Math.ceil(parts(date)[2] / 7);
}
/** True when a date is its weekday's last in the month. */
export function isLastOfMonth(date) {
    const [y, m, d] = parts(date);
    return d + 7 > daysInMonth(y, m);
}
/** The nth (or last) `weekday` of a month, or null when there is no fifth. */
function nthWeekday(year, month, weekday, nth) {
    const days = daysInMonth(year, month);
    if (nth === -1) {
        const lastWeekday = weekdayIndex(dayString(year, month, days));
        return dayString(year, month, days - ((lastWeekday - weekday + 7) % 7));
    }
    const firstWeekday = weekdayIndex(dayString(year, month, 1));
    const day = 1 + ((weekday - firstWeekday + 7) % 7) + (nth - 1) * 7;
    return day <= days ? dayString(year, month, day) : null;
}
/** The month `n` months after (year, month). */
function monthPlus(year, month, n) {
    const index = year * 12 + (month - 1) + n;
    return [Math.floor(index / 12), (index % 12) + 1];
}
// ---------------------------------------------------------------------------
// The one expansion
// ---------------------------------------------------------------------------
/** The days of the series in order from its first, without end (the caller stops). */
function* seriesDays(first, r) {
    const interval = Math.max(1, r.interval);
    if (r.freq === 'daily') {
        for (let date = first;; date = addDays(date, interval))
            yield date;
    }
    if (r.freq === 'weekly') {
        const days = r.weekdays ?? [weekdayIndex(first)];
        const sunday = addDays(first, -weekdayIndex(first));
        for (let week = sunday;; week = addDays(week, 7 * interval)) {
            for (const day of days) {
                const date = addDays(week, day);
                if (date >= first)
                    yield date;
            }
        }
    }
    const [y, m, d] = parts(first);
    const weekday = weekdayIndex(first);
    const step = r.freq === 'yearly' ? 12 * interval : interval;
    for (let n = 0;; n += step) {
        const [year, month] = monthPlus(y, m, n);
        const date = r.monthNth !== null
            ? nthWeekday(year, month, weekday, r.monthNth)
            : dayString(year, month, Math.min(d, daysInMonth(year, month)));
        if (date && date >= first)
            yield date;
    }
}
/** A guard against a pattern that never reaches `to` (it cannot, but a loop must end). */
const MAX_STEPS = 20_000;
/**
 * Every day of the series inside `[from, to]` (inclusive), counted from
 * `first`, so *after 10 occurrences* means the first ten whatever window is
 * asked about. `until` and `count` end it; `to` stops the reading.
 */
export function seriesDates(first, r, from, to) {
    const last = r.until && r.until < to ? r.until : to;
    const out = [];
    let seen = 0;
    let steps = 0;
    for (const date of seriesDays(first, r)) {
        steps += 1;
        if (date > last || steps > MAX_STEPS)
            break;
        seen += 1;
        if (r.count !== null && seen > r.count)
            break;
        if (date >= from)
            out.push(date);
        /* A daily series asked about a far window skips ahead rather than walking every day. */
        if (r.freq === 'daily' && r.count === null && date < from) {
            const gap = daysBetween(date, from);
            if (gap > r.interval * 2) {
                const jump = Math.floor(gap / r.interval) - 1;
                return occurrenceDatesFrom(addDays(date, jump * r.interval), r, from, last, out);
            }
        }
    }
    return out;
}
/** The rest of a daily series from `start`, when nothing is counting. */
function occurrenceDatesFrom(start, r, from, last, out) {
    for (let date = start; date <= last; date = addDays(date, r.interval)) {
        if (date >= from)
            out.push(date);
    }
    return out;
}
/** The first day of the series after `after` (exclusive), or null when it has ended. */
export function nextOccurrence(first, r, after) {
    let seen = 0;
    let steps = 0;
    for (const date of seriesDays(first, r)) {
        steps += 1;
        if (steps > MAX_STEPS || (r.until && date > r.until))
            return null;
        seen += 1;
        if (r.count !== null && seen > r.count)
            return null;
        if (date > after)
            return date;
    }
    return null;
}
/** Whether `day` is one of the series' own days. */
export function fallsOn(first, r, day) {
    return seriesDates(first, r, day, day).length === 1;
}
/**
 * **The one expansion with its exceptions applied** — the calendar, the stop
 * card, a task list's next meeting and Delete all read it: the series' days
 * inside `[from, to]` less those taken out, a moved one on the day it was
 * moved to (whether or not its own day is inside the window), and in date
 * order. A move whose own day is no longer one of the series' (the series
 * was changed after it) names nothing and is left out; so is a move whose day
 * was taken out. An occurrence changed in anything else (0152) carries that
 * change as its `edit`, by the same rule: one naming a day the series no
 * longer falls on, or a day taken out, names nothing.
 */
export function seriesOccurrences(first, r, from, to, exceptions = {}) {
    const skips = new Set(exceptions.skips ?? []);
    const moves = new Map((exceptions.moves ?? []).map((move) => [move.on, move]));
    const edits = new Map((exceptions.edits ?? []).map((edit) => [edit.on, edit]));
    const out = [];
    const inside = (day) => day >= from && day <= to;
    const one = (on, date, moved) => {
        const edit = edits.get(on);
        return edit ? { on, date, moved, edit } : { on, date, moved };
    };
    for (const on of seriesDates(first, r, from, to)) {
        if (skips.has(on))
            continue;
        const moved = moves.get(on) ?? null;
        if (!moved)
            out.push(one(on, on, null));
        else if (inside(moved.date))
            out.push(one(on, moved.date, moved));
    }
    for (const moved of moves.values()) {
        if (inside(moved.on) || skips.has(moved.on) || !inside(moved.date))
            continue;
        if (fallsOn(first, r, moved.on))
            out.push(one(moved.on, moved.date, moved));
    }
    return out.sort((a, b) => a.date.localeCompare(b.date) || (a.moved?.start ?? -1) - (b.moved?.start ?? -1)
        || a.on.localeCompare(b.on));
}
/**
 * The series moved by `days` — *Every one* dropped on another day: its days
 * of the week and its last day move with it, so every occurrence lands the
 * same number of days on and the series still has as many. (A month's day
 * number and its *nth weekday* are its first date's, which moves too.)
 */
export function shiftedRecurrence(r, days) {
    if (days === 0)
        return r;
    return {
        ...r,
        weekdays: r.weekdays
            ? [...new Set(r.weekdays.map((day) => (((day + days) % 7) + 7) % 7))].sort((a, b) => a - b) : null,
        until: r.until ? addDays(r.until, days) : null,
    };
}
/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysFrom(from, to) {
    return daysBetween(from, to);
}
// ---------------------------------------------------------------------------
// *Every one* dropped on another day (Aaron, Oct 25, answer 2.1)
// ---------------------------------------------------------------------------
/**
 * Whether a pattern names its days of the week — *Every weekday*, a week on
 * two or more days ticked (every N weeks too), a month's or a year's *fourth
 * Tuesday*. Such a series keeps its days when one of its occurrences is moved
 * *Every one* to another day, and only its time changes (Aaron, Oct 25:
 * "agreed" — an every-weekday meeting dragged Wednesday → Thursday used to
 * become Tuesday to Saturday).
 */
export function namesWeekdays(r) {
    if (r.freq === 'weekly')
        return (r.weekdays?.length ?? 1) > 1;
    return r.monthNth !== null;
}
/**
 * *Every one* for the occurrence whose own day is `on`, dropped on `to` —
 * the one rule the server applies and the question words. A pattern that
 * names its weekdays keeps them (`namesWeekdays`). Anything else moves as
 * many days, so the day dropped on is one of its days: a week on one day
 * becomes a week on the new one (*every Tuesday* dragged to Wednesday is
 * *every Wednesday*), every day or every N days starts as many days on, and a
 * month or a year by its date moves to the new date.
 */
export function seriesMove(first, r, on, to) {
    const shift = namesWeekdays(r) ? 0 : daysBetween(on, to);
    if (shift === 0)
        return { shift, first, recurrence: r };
    return { shift, first: addDays(first, shift), recurrence: shiftedRecurrence(r, shift) };
}
/** *Mon–Fri*, *Mon and Wed*, *the fourth Tuesday* — the days a series that names them keeps. */
function keptDaysWords(first, r) {
    if (r.freq === 'weekly') {
        const days = r.weekdays ?? [weekdayIndex(first)];
        if (isWorkWeekdays(days))
            return 'Mon–Fri';
        if (days.length === 7)
            return 'every day';
        return listWords(days.map((day) => WEEKDAYS[day].slice(0, 3)));
    }
    const nth = NTH_WORDS[r.monthNth ?? 1];
    const weekday = WEEKDAYS[weekdayIndex(first)];
    return r.freq === 'yearly' ? `the ${nth} ${weekday} of ${MONTHS[parts(first)[1] - 1]}` : `the ${nth} ${weekday}`;
}
/** *every Wednesday*, *every other Wednesday*, *every day*, *every month on day 29* — a moved series' new days. */
function movedDaysWords(first, r) {
    if (r.freq === 'weekly') {
        const weekday = WEEKDAYS[(r.weekdays ?? [weekdayIndex(first)])[0]];
        return r.interval === 1 ? `every ${weekday}` : r.interval === 2 ? `every other ${weekday}` : `every ${r.interval} weeks on ${weekday}`;
    }
    return repeatPatternWords(first, r);
}
/** *moves to 2:00 PM*, *stays at 9:00 AM*, *9:00–10:30 AM* — the time a change gives, its end only when the length changed. */
function timeWords(to, was) {
    if (to.minutes !== was.minutes)
        return clockRange(to.start, to.start + to.minutes);
    return `${to.start === was.start ? 'stays at' : 'moves to'} ${clock(to.start)}`;
}
/**
 * What *Just this one* and *Every one* will each do, said before either is
 * pressed (Aaron, Oct 25, answer 2.1: the question's words say what will
 * happen) — `was` is the occurrence where it is drawn, `to` where it was put.
 *
 * - *Just this one — Thu, Oct 29 at 2:00 PM*
 * - *Every one — keeps Mon–Fri, moves to 2:00 PM* (a pattern that names its weekdays)
 * - *Every one — every Wednesday at 2:00 PM* (one dropped on another day)
 * - *Every one — moves to 2:00 PM* · *Every one — 9:00–10:30 AM* (the same day, another time or length)
 */
export function whichWords(first, r, on, was, to) {
    const sameLength = to.minutes === was.minutes;
    const one = `${dayComma(to.date)} ${sameLength ? `at ${clock(to.start)}` : `· ${clockRange(to.start, to.start + to.minutes)}`}`;
    if (namesWeekdays(r))
        return { one, all: `keeps ${keptDaysWords(first, r)}, ${timeWords(to, was)}` };
    const moved = seriesMove(first, r, on, to.date);
    if (moved.shift === 0)
        return { one, all: timeWords(to, was) };
    const days = movedDaysWords(moved.first, moved.recurrence);
    /* Every day is every day wherever it starts, so say how far each one moves. */
    const each = r.freq === 'daily'
        ? `, each ${Math.abs(moved.shift) === 1 ? 'a day' : `${Math.abs(moved.shift)} days`} ${moved.shift > 0 ? 'later' : 'earlier'}` : '';
    const time = sameLength ? `at ${clock(to.start)}` : clockRange(to.start, to.start + to.minutes);
    return { one, all: `${days}${each}${sameLength && !each ? ' ' : ', '}${time}` };
}
// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December'];
const NTH_WORDS = { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', [-1]: 'last' };
const UNIT = {
    daily: ['day', 'days'], weekly: ['week', 'weeks'], monthly: ['month', 'months'], yearly: ['year', 'years'],
};
function listWords(items) {
    if (items.length <= 1)
        return items[0] ?? '';
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
/** *Sep 30, 2027*. */
function shortDate(date) {
    const [y, m, d] = parts(date);
    return `${MONTHS[m - 1].slice(0, 3)} ${d}, ${y}`;
}
/** *every week on Tuesday* — the pattern alone, lower-case, for a sentence to carry. */
export function repeatPatternWords(first, r) {
    const [singular, plural] = UNIT[r.freq];
    const every = r.interval === 1 ? `every ${singular}` : `every ${r.interval} ${plural}`;
    const weekday = WEEKDAYS[weekdayIndex(first)];
    if (r.freq === 'daily')
        return every;
    if (r.freq === 'weekly') {
        /* *Every weekday* (Aaron, Oct 24, note 1.3) — and *every 2 weeks on weekdays*. */
        if (isWorkWeekdays(r.weekdays))
            return r.interval === 1 ? 'every weekday' : `${every} on weekdays`;
        const days = (r.weekdays ?? [weekdayIndex(first)]).map((day) => WEEKDAYS[day]);
        return `${every} on ${days.length === 7 ? 'every day' : listWords(days)}`;
    }
    const [, m, d] = parts(first);
    if (r.freq === 'monthly') {
        return r.monthNth !== null ? `${every} on the ${NTH_WORDS[r.monthNth]} ${weekday}` : `${every} on day ${d}`;
    }
    return r.monthNth !== null
        ? `${every} on the ${NTH_WORDS[r.monthNth]} ${weekday} of ${MONTHS[m - 1]}`
        : `${every} on ${MONTHS[m - 1]} ${d}`;
}
/** How a series ends: *until Sep 30, 2027*, *10 times*, or nothing when it never does. */
export function repeatEndWords(r) {
    return r.until ? `until ${shortDate(r.until)}` : r.count !== null ? `${r.count} ${r.count === 1 ? 'time' : 'times'}` : '';
}
/** *Every week on Tuesday*, *Every 2 months on the last Friday · 10 times*, *… · until Sep 30, 2027*. */
export function recurrenceWords(first, r) {
    const pattern = repeatPatternWords(first, r);
    const end = repeatEndWords(r);
    return `${pattern.charAt(0).toUpperCase()}${pattern.slice(1)}${end ? ` · ${end}` : ''}`;
}
/**
 * The presets a meeting's *Repeats* offers before *Custom…* (§9.6 plus
 * Outlook's) — *Every weekday* among them (Aaron, Oct 24, note 1.3: "Needs an
 * every weekday option"), Monday to Friday whatever day the first one is.
 */
export function repeatPresets(first) {
    const base = { weekdays: null, monthNth: null, until: null, count: null };
    const weekday = WEEKDAYS[weekdayIndex(first)];
    return [
        { key: 'never', label: 'Never', recurrence: null },
        { key: 'daily', label: 'Every day', recurrence: { ...base, freq: 'daily', interval: 1 } },
        { key: 'weekdays', label: 'Every weekday', recurrence: { ...base, freq: 'weekly', interval: 1, weekdays: [...WORK_WEEKDAYS] } },
        { key: 'weekly', label: `Every week on ${weekday}`, recurrence: { ...base, freq: 'weekly', interval: 1 } },
        { key: 'every_other_week', label: 'Every other week', recurrence: { ...base, freq: 'weekly', interval: 2 } },
        { key: 'monthly', label: 'Every month', recurrence: { ...base, freq: 'monthly', interval: 1 } },
        { key: 'yearly', label: 'Every year', recurrence: { ...base, freq: 'yearly', interval: 1 } },
    ];
}
/**
 * Whether `repeat_rule` alone says all of it — the presets a reader of the
 * rule by itself words correctly (*Tuesdays*, *Every other Tuesday*). *Every
 * weekday* is a preset whose rule says only *weekly*, so it goes with its
 * words like a custom one.
 */
export function ruleSaysAll(r) {
    const key = presetKeyOf(r);
    return key !== null && key !== 'weekdays';
}
/** Which preset a recurrence is, or null when it is a custom one. */
export function presetKeyOf(r) {
    if (!r)
        return 'never';
    if (r.until || r.count !== null || r.monthNth !== null)
        return null;
    if (r.freq === 'weekly' && r.interval === 1 && isWorkWeekdays(r.weekdays))
        return 'weekdays';
    if (r.freq === 'weekly' && r.weekdays && r.weekdays.length > 1)
        return null;
    if (r.freq === 'weekly' && r.interval === 2)
        return r.weekdays ? null : 'every_other_week';
    if (r.interval !== 1)
        return null;
    if (r.freq === 'weekly' && r.weekdays)
        return null;
    return r.freq;
}
//# sourceMappingURL=recurrence.js.map